import React, { useEffect, useState } from "react";
import useQuery from "../../../../hooks/useQuery";
import { Button, Segmented } from "antd";
import { getDischargeSummaryApi } from "../../../../services/discharge.services";
import NoDataPage from "../../../NoDataPages/NoDataPage";
import dischargeImage from "../../../../assets/images/nurseIcons/dischargeSummary.svg";
import FormContentLoader from "../../FormsIp/FormContentLoader";
import ShareModal from "./ShareModal";
import ScaledHtmlPreview from "../../../ui/ScaledHtmlPreview";
import useIsMobile from "../../../../hooks/useIsMobile";
import FormHistoryCards from "../../FormsIp/CommonComponents/FormHistoryCards";
import {
  extractSummaryHistory,
  extractSummaryHtml,
} from "./dischargeSummaryData";
import "../../FormsIp/CommonComponents/FormDocument.css";
import "./DischargeSummaryToolbar.css";
import "./DischargeSummaryMobile.css";
import { sanitizeDocumentHtml } from "../../../../helpers/sanitizeHtml";

const ROLE_OPTIONS = [
  { label: "Doctor", value: "Doctor" },
  { label: "Physiotherapist", value: "Physiotherapist" },
];

const ReceptionistDischargeSummary = ({ patientData }) => {
  const params = useQuery();
  const isMobile = useIsMobile();
  const [currTab, setCurrTab] = useState("Doctor");
  const [summaryData, setSummaryData] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openModal, setOpenModal] = useState(false);

  useEffect(() => {
    if (!params?.patient) return;

    let cancelled = false;
    setLoading(true);

    getDischargeSummaryApi(params.patient, `${currTab}&print=True`)
      .then((res) => {
        if (!cancelled) {
          setSummaryData(extractSummaryHtml(res?.data));
          setHistory(extractSummaryHistory(res?.data));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSummaryData(null);
          setHistory([]);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [currTab, params?.patient]);

  const onChange = (value) => {
    if (value === currTab) return;
    setSummaryData(null);
    setHistory([]);
    setCurrTab(value);
  };

  const printDischargeSummary = async () => {
    try {
      const resp = await getDischargeSummaryApi(
        params?.patient,
        `${currTab}&print=True`
      );
      const html = extractSummaryHtml(resp?.data);
      if (html) {
        const printDocument = document.createElement("div");
        printDocument.innerHTML = sanitizeDocumentHtml(html);

        const iframe = document.createElement("iframe");
        document.body.appendChild(iframe);

        const iframeDoc =
          iframe.contentDocument || iframe.contentWindow.document;
        iframeDoc.open();
        iframeDoc.write(printDocument.outerHTML);
        iframeDoc.close();

        setTimeout(() => {
          iframe.contentWindow.print();
          document.body.removeChild(iframe);
        }, 300);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const actionsDisabled = loading || !summaryData;

  return (
    <div className="discharge-summary-view">
      <div className="ds-toolbar">
        <Segmented
          className="ds-role-segment"
          options={ROLE_OPTIONS}
          value={currTab}
          onChange={onChange}
        />

        <div className="ds-toolbar__actions">
          <Button
            type="primary"
            className="ds-action-btn"
            onClick={() => setOpenModal(true)}
            disabled={actionsDisabled}
          >
            {isMobile ? "Share Summary" : "Share Discharge Summary"}
          </Button>
          <Button
            className="ds-action-btn ds-action-btn--secondary"
            onClick={printDischargeSummary}
            disabled={actionsDisabled}
          >
            {isMobile ? "Print Summary" : "Print Discharge Summary"}
          </Button>
        </div>
      </div>

      <FormContentLoader
        loading={loading}
        tip={`Loading ${currTab} discharge summary...`}
      >
        {summaryData ? (
          <>
            {history.length > 0 && (
              <div className="ds-history-row">
                <FormHistoryCards entries={history} />
              </div>
            )}
            <ScaledHtmlPreview
              html={summaryData}
              designWidth={800}
              className="ip-form-html-preview"
            />
            {openModal && (
              <ShareModal
                openModal={openModal}
                setOpenModal={setOpenModal}
                id={params?.patient}
                practitioner={currTab}
                patientData={patientData}
              />
            )}
          </>
        ) : (
          <NoDataPage
            title="Discharge Summary Not generated"
            image={dischargeImage}
          />
        )}
      </FormContentLoader>
    </div>
  );
};

export default ReceptionistDischargeSummary;
